import { HTTP_STATUS_INTERNAL_SERVER_ERROR } from '../../../../constants/api'
import { EMPTY_LIST } from '../../../../constants/common'
import { RENTAL_PORTAL_MOCK_STATE } from '../../../../constants/rentalPortalMockState'
import { hasRentalPortalMockState } from '../../../../utils/rentalPortalMockState'

import {
  IMPORT_CHANGE_OUTCOME,
  IMPORT_LOG_FILTER_ALL,
  IMPORT_RUN_STATUS,
} from './IntegrationsImportLog.constants'

const MOCK_IMPORT_LOG_READ_LATENCY = 320

const MOCK_IMPORT_LOG_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_IMPORT_LOG_WRITE_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_IMPORT_SOURCE_LABEL = 'RELion ONE'

const MOCK_IMPORT_RUN_HOURS = 4

const MOCK_IMPORT_RUN_MINUTES = 12

const MOCK_IMPORT_RUN_ANCHOR = '2026-09-17T12:05:00+02:00'

const MOCK_IMPORT_RUN_SHAPES = [
  [IMPORT_RUN_STATUS.success, 1284, 3401, 0, 2],
  [IMPORT_RUN_STATUS.success, 1284, 12, 0, 0],
  [IMPORT_RUN_STATUS.partial, 1284, 980, 14, 0],
  [IMPORT_RUN_STATUS.failed, 0, 0, 0, 0],
  [IMPORT_RUN_STATUS.success, 1281, 44, 0, 1],
  [IMPORT_RUN_STATUS.success, 1281, 7, 0, 0],
  [IMPORT_RUN_STATUS.partial, 1279, 655, 3, 4],
  [IMPORT_RUN_STATUS.success, 1279, 18, 0, 0],
  [IMPORT_RUN_STATUS.success, 1275, 26, 0, 0],
  [IMPORT_RUN_STATUS.success, 1275, 9, 0, 0],
  [IMPORT_RUN_STATUS.failed, 0, 0, 0, 0],
  [IMPORT_RUN_STATUS.success, 1270, 51, 0, 3],
]

function toMockImportRun([status, records, applied, blocked, held], index) {
  const startedAt = new Date(MOCK_IMPORT_RUN_ANCHOR)

  startedAt.setHours(startedAt.getHours() - index * MOCK_IMPORT_RUN_HOURS)

  const finishedAt = new Date(startedAt)

  finishedAt.setMinutes(finishedAt.getMinutes() + MOCK_IMPORT_RUN_MINUTES)

  return {
    fields_applied: applied,
    fields_blocked: blocked,
    fields_held: held,
    finished_at:
      status === IMPORT_RUN_STATUS.running ? null : finishedAt.toISOString(),
    id: index + 1,
    records_received: records,
    source_label: MOCK_IMPORT_SOURCE_LABEL,
    source_system: 'preloader',
    started_at: startedAt.toISOString(),
    status,
    trigger: 'schedule',
  }
}

const MOCK_IMPORT_RUNS = MOCK_IMPORT_RUN_SHAPES.map(toMockImportRun)

const MOCK_IMPORT_CHANGE_SHAPES = [
  [
    'Flat',
    'Top 4, Musterstraße 12',
    'area',
    '74',
    '76',
    IMPORT_CHANGE_OUTCOME.applied,
  ],
  [
    'Flat',
    'Top 4, Musterstraße 12',
    'room_count',
    '2',
    '3',
    IMPORT_CHANGE_OUTCOME.applied,
  ],
  [
    'Flat',
    'Top 9, Musterstraße 12',
    'rent_gross',
    '1.480',
    '1.560',
    IMPORT_CHANGE_OUTCOME.applied,
  ],
  [
    'Flat',
    'Top 12, Lindenweg 3',
    'info',
    'Renoviert 2025',
    'Bezugsfertig ab 10/2026',
    IMPORT_CHANGE_OUTCOME.held,
  ],
  [
    'Listing',
    'Top 18, Beethovenstraße 12',
    'rent_price',
    '990',
    '1.010',
    IMPORT_CHANGE_OUTCOME.blockedPortalOwned,
  ],
  [
    'Listing',
    'Top 30, Musterstraße 12',
    'description',
    'Helle Wohnung …',
    'Sanierte Altbauwohnung …',
    IMPORT_CHANGE_OUTCOME.blockedPortalOwned,
  ],
  [
    'Building',
    'Musterstraße 12',
    'build_year',
    '1964',
    '1965',
    IMPORT_CHANGE_OUTCOME.applied,
  ],
  [
    'Building',
    'Lindenweg 3',
    'unit_count',
    '18',
    '19',
    IMPORT_CHANGE_OUTCOME.applied,
  ],
  [
    'Contract',
    'V-2026-0114',
    'end_date',
    '31.12.2026',
    '31.03.2027',
    IMPORT_CHANGE_OUTCOME.applied,
  ],
  [
    'Contract',
    'V-2026-0087',
    'note',
    'Staffelmiete',
    'Staffelmiete ab 2027',
    IMPORT_CHANGE_OUTCOME.held,
  ],
  [
    'User',
    'Katharina Wolf',
    'phone',
    null,
    null,
    IMPORT_CHANGE_OUTCOME.blockedUndeclared,
  ],
  ['User', 'Michael Hofer', 'email', null, null, IMPORT_CHANGE_OUTCOME.applied],
]

function toMockImportChange(
  [entity, recordLabel, field, oldValue, newValue, outcome],
  index
) {
  const isMasked = oldValue === null && newValue === null

  return {
    created: MOCK_IMPORT_RUN_ANCHOR,
    entity,
    external_id: `EXT-${1000 + index}`,
    field,
    id: index + 1,
    new_value: isMasked ? { masked: true, value: null } : newValue,
    old_value: isMasked ? { masked: true, value: null } : oldValue,
    outcome,
    record_id: 100 + index,
    record_label: recordLabel,
    resolved_by: null,
  }
}

const MOCK_IMPORT_CHANGES = MOCK_IMPORT_CHANGE_SHAPES.map(toMockImportChange)

function withMockLatency() {
  return new Promise(resolve => {
    setTimeout(resolve, MOCK_IMPORT_LOG_READ_LATENCY)
  })
}

function toMockImportRunPermitted(run) {
  return {
    ...run,
    permissions: {
      can_reprocess: !hasRentalPortalMockState(
        RENTAL_PORTAL_MOCK_STATE.readOnly
      ),
    },
  }
}

function toMockPage({ items, page, pageSize }) {
  const pageCount = Math.ceil(items.length / pageSize)
  const currentPage = Math.min(page, Math.max(pageCount, 1))
  const firstIndex = (currentPage - 1) * pageSize

  return {
    items: items.slice(firstIndex, firstIndex + pageSize),
    pagination: {
      page: currentPage,
      page_count: pageCount,
      page_size: pageSize,
      total: items.length,
    },
  }
}

export function getMockImportRunSources() {
  return [...new Set(MOCK_IMPORT_RUNS.map(run => run.source_label))]
}

export function getMockImportRuns({
  page,
  page_size: pageSize,
  source,
  status,
}) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_IMPORT_LOG_READ_ERROR)
    }

    const runs = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : MOCK_IMPORT_RUNS.filter(
          run =>
            (status === IMPORT_LOG_FILTER_ALL || run.status === status) &&
            (source === IMPORT_LOG_FILTER_ALL || run.source_label === source)
        )

    return {
      data: toMockPage({
        items: runs.map(toMockImportRunPermitted),
        page,
        pageSize,
      }),
    }
  })
}

export function getMockImportRun(runId) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_IMPORT_LOG_READ_ERROR)
    }

    const run = MOCK_IMPORT_RUNS.find(item => item.id === Number(runId))

    return run ? toMockImportRunPermitted(run) : null
  })
}

function getMockImportRunChangeList({ outcome, runId }) {
  const run = MOCK_IMPORT_RUNS.find(item => item.id === Number(runId))

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty) ||
    run?.status === IMPORT_RUN_STATUS.failed
    ? EMPTY_LIST
    : MOCK_IMPORT_CHANGES.filter(
        change =>
          outcome === IMPORT_LOG_FILTER_ALL || change.outcome === outcome
      )
}

export function getMockImportRunChanges({
  outcome,
  page,
  page_size: pageSize,
  run_id: runId,
}) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_IMPORT_LOG_READ_ERROR)
    }

    return {
      data: toMockPage({
        items: getMockImportRunChangeList({ outcome, runId }),
        page,
        pageSize,
      }),
    }
  })
}

export function getMockImportRunChangesExport({ outcome, run_id: runId }) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_IMPORT_LOG_READ_ERROR)
    }

    return { data: { items: getMockImportRunChangeList({ outcome, runId }) } }
  })
}

export function performMockImportRunReprocess(runId) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_IMPORT_LOG_WRITE_ERROR)
    }

    const run = MOCK_IMPORT_RUNS.find(item => item.id === Number(runId))

    run.status = IMPORT_RUN_STATUS.running
    run.finished_at = null

    return { data: toMockImportRunPermitted(run) }
  })
}
